import React from "react";
import { Wallet } from "lucide-react";
import { HomePanel, PanelEmpty } from "./HomePanel";
import { fmtMoney } from "@/portal/format";

function Money({ label, value, pct, className = "", testid }) {
  return (
    <div className={`min-w-0 ${className}`} data-testid={testid}>
      <div className="cp-meta">{label}</div>
      <div className="cp-serif text-[15px] text-cp-text leading-tight mt-0.5 whitespace-nowrap">{value}</div>
      {pct != null && <div className="cp-meta mt-0.5">({pct}%)</div>}
    </div>
  );
}

/** „Buget & Costuri” — sumar numeric + bară Consumat / Estimat / Rămas. */
export function BudgetCard({ project, budget }) {
  const b = budget;
  const to = project ? `/portal/proiecte/${project.id}?tab=buget` : undefined;
  if (!b) {
    return (
      <HomePanel icon={Wallet} title="Buget & Costuri" to={to} linkLabel="Vezi detalii" testid="home-budget">
        <PanelEmpty icon={Wallet} text="Informațiile de buget nu au fost încă publicate de echipa Brisk." />
      </HomePanel>
    );
  }
  const cur = b.currency;
  const approved = b.approved != null ? fmtMoney(b.approved, cur) : b.approved_display || "—";
  const consumed = b.consumed != null ? fmtMoney(b.consumed, cur) : b.consumed_display || "—";
  const forecast = b.forecast != null ? fmtMoney(b.forecast, cur) : b.forecast_display || "—";
  const scale = Math.max(b.approved || 0, b.forecast || 0, b.consumed || 0);
  const pct = (x) => (scale && x ? Math.min(100, Math.max(0, (x / scale) * 100)) : 0);
  const cPct = pct(b.consumed);
  const fPct = Math.max(0, pct(b.forecast) - cPct);
  const aPct = pct(b.approved);
  const hasBar = scale > 0 && b.approved;

  return (
    <HomePanel icon={Wallet} title="Buget & Costuri" to={to} linkLabel="Vezi detalii" testid="home-budget">
      <div className="grid grid-cols-3 gap-2 min-w-0">
        <Money label="Buget aprobat" value={approved} testid="budget-approved" />
        <Money label="Consumat" value={consumed} pct={b.consumed_pct} className="pl-2.5 border-l border-cp-border" testid="budget-consumed" />
        <Money label="Estimat final" value={forecast} pct={b.forecast_pct} className="pl-2.5 border-l border-cp-border" testid="budget-forecast" />
      </div>
      {hasBar && (
        <div className="mt-5" data-testid="budget-bar">
          <div className="relative h-[14px] rounded-full bg-[#E4EAF3] overflow-visible">
            <div className="absolute inset-y-0 left-0 rounded-l-full bg-cp-accent" style={{ width: `${cPct}%`, borderRadius: fPct ? undefined : undefined }} title={`Consumat ${b.consumed_pct ?? ""}%`} />
            {fPct > 0 && <div className="absolute inset-y-0 bg-[#8FB0EA]" style={{ left: `${cPct}%`, width: `${fPct}%` }} title={`Estimat final ${b.forecast_pct ?? ""}%`} />}
            {aPct > 0 && aPct < 100 && <span className="absolute -top-1 -bottom-1 w-px border-l border-dashed border-cp-text" style={{ left: `${aPct}%` }} aria-hidden="true" />}
          </div>
          <div className="flex items-center justify-between mt-2 text-[11.5px] text-cp-text-3">
            <div className="flex items-center gap-3.5 flex-wrap">
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-cp-accent" />Consumat</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#8FB0EA]" />Estimat</span>
              <span className="inline-flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#E4EAF3] ring-1 ring-cp-border" />Rămas</span>
            </div>
            <span className="whitespace-nowrap">Buget aprobat</span>
          </div>
        </div>
      )}
    </HomePanel>
  );
}
